import { format } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FiArrowLeft,
  FiArrowRight,
  FiClipboard,
  FiClock,
  FiFileText,
  FiGlobe,
  FiGrid,
  FiHash,
  FiInfo,
  FiLink,
  FiMessageSquare,
  FiPaperclip,
  FiSettings,
} from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { CLOSED_STATUS_NAME } from '../../models/helpdeskConstants'

import {
  useCreateHelpdeskTicketMessageMutation,
  useGetMyHelpdeskTicketQuery,
  useGetMyHelpdeskTicketStatusHistoryQuery,
} from '../../api/services/helpdeskApi'
import AttachmentsViewModal from '../../components/helpdesk/AttachmentsViewModal'
import StatusHistoryModal from '../../components/helpdesk/StatusHistoryModal'
import HelpDeskReplyComposer from '../../components/helpdesk/ticket-detail/HelpDeskReplyComposer'
import MessageAttachments from '../../components/helpdesk/ticket-detail/MessageAttachments'
import MessageSignature from '../../components/helpdesk/ticket-detail/MessageSignature'
import Spinner from '../../components/other/Spinner'
import { sanitizeMessageHtml } from '../../utils/sanitize-message-html.utils'

const revokeAttachmentPreviews = (attachments) => {
  attachments.forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl))
}

const authorBadgeClass = (authorType) => {
  if (authorType === 'support') return 'bg-blue-100 text-blue-800'
  if (authorType === 'client') return 'bg-emerald-100 text-emerald-800'
  return 'bg-gray-100 text-gray-700'
}

const authorBadgeLabel = (authorType, t) => {
  if (authorType === 'support') return t('support.detail.author_support')
  if (authorType === 'client') return t('support.detail.author_client')
  return t('support.detail.author_system')
}

const messageBubbleClass = (authorType) => {
  if (authorType === 'system') return 'border-sky-100 bg-sky-50'
  if (authorType === 'support') return 'border-emerald-100 bg-emerald-50/70'
  return 'border-slate-100 bg-white'
}

const statusTone = (name = '') => {
  const normalizedName = name.toLowerCase()
  if (normalizedName.includes('nov')) return 'bg-violet-100 text-violet-800'
  if (normalizedName.includes('rješ') || normalizedName.includes('zatvor')) return 'bg-emerald-100 text-emerald-800'
  if (normalizedName.includes('ček') || normalizedName.includes('obradi') || normalizedName.includes('radu')) {
    return 'bg-sky-100 text-sky-800'
  }
  return 'bg-slate-100 text-slate-700'
}

const priorityDot = (name = '') => {
  const normalizedName = name.toLowerCase()
  if (normalizedName.includes('visok') || normalizedName.includes('krit') || normalizedName.includes('hit')) {
    return 'bg-rose-500'
  }
  if (normalizedName.includes('sred')) return 'bg-amber-400'
  if (normalizedName.includes('nisk')) return 'bg-emerald-500'
  return 'bg-slate-400'
}

const DetailRow = ({ icon, label, value }) => (
  <div className="flex gap-3 border-b border-slate-100 py-3.5 last:border-0">
    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500">
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-900">{value}</dd>
    </div>
  </div>
)

const HelpDeskTicketDetail = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { ticketId } = useParams()
  const [messageBody, setMessageBody] = useState('')
  const [messageHtml, setMessageHtml] = useState('')
  const [messageAttachments, setMessageAttachments] = useState([])
  const messageAttachmentsRef = useRef([])
  const [showAttachments, setShowAttachments] = useState(false)
  const [showStatusHistory, setShowStatusHistory] = useState(false)
  const [additionalInfoTab, setAdditionalInfoTab] = useState('information')

  const { data: ticket, isLoading, isError } = useGetMyHelpdeskTicketQuery(ticketId ?? '', { skip: !ticketId })

  const isClosed = ticket?.status?.name === CLOSED_STATUS_NAME

  const {
    data: statusHistory,
    isFetching: isHistoryLoading,
    isError: isHistoryError,
  } = useGetMyHelpdeskTicketStatusHistoryQuery(ticketId ?? '', {
    skip: !ticketId || (additionalInfoTab !== 'history' && !showStatusHistory),
  })

  const [createMessage, { isLoading: isSending }] = useCreateHelpdeskTicketMessageMutation()

  useEffect(() => {
    messageAttachmentsRef.current = messageAttachments
  }, [messageAttachments])

  useEffect(
    () => () => {
      revokeAttachmentPreviews(messageAttachmentsRef.current)
      messageAttachmentsRef.current = []
    },
    [],
  )

  useEffect(() => {
    revokeAttachmentPreviews(messageAttachmentsRef.current)
    messageAttachmentsRef.current = []
    setMessageBody('')
    setMessageHtml('')
    setMessageAttachments([])
  }, [ticketId])

  const handleMessageChange = ({ html, text, activeAttachmentIds }) => {
    setMessageHtml(html)
    setMessageBody(text)
    const activeIds = new Set(activeAttachmentIds)
    setMessageAttachments((previous) => {
      previous
        .filter((attachment) => !activeIds.has(attachment.localId))
        .forEach((attachment) => URL.revokeObjectURL(attachment.previewUrl))
      return previous.filter((attachment) => activeIds.has(attachment.localId))
    })
  }

  const handleSendMessage = async (event) => {
    event.preventDefault()
    const body = messageBody.trim()
    if (!ticketId || isClosed || (!body && messageAttachments.length === 0)) return

    try {
      const formData = new FormData()
      formData.append('ticket_id', String(ticketId))
      formData.append('body', body)
      if (messageHtml) formData.append('body_html', messageHtml)
      messageAttachments.forEach((attachment) => formData.append('files', attachment.file))
      if (messageAttachments.length) {
        formData.append(
          'inline_attachment_ids',
          JSON.stringify(messageAttachments.map((attachment) => attachment.localId)),
        )
      }

      await createMessage({ ticket_id: Number(ticketId), formData }).unwrap()
      revokeAttachmentPreviews(messageAttachments)
      messageAttachmentsRef.current = []
      setMessageBody('')
      setMessageHtml('')
      setMessageAttachments([])
      toast.success(t('support.detail.comment_success'))
    } catch (error) {
      toast.error(error?.data?.message || t('support.detail.comment_error'))
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (isError || !ticket) {
    return (
      <div>
        <p className="text-sm text-red-600 mb-3">{t('support.detail.not_found')}</p>
        <button type="button" onClick={() => navigate('/helpdesk')} className="text-sm text-blue-600 hover:underline">
          {t('support.detail.back_to_list')}
        </button>
      </div>
    )
  }

  return (
    <div className="mx-auto min-h-[calc(100vh-12vh)] space-y-4 overflow-y-auto px-1 py-2 w-full">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate('/helpdesk')}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
        >
          <FiArrowLeft className="h-4 w-4" />
          {t('support.detail.back_to_list')}
        </button>
        <p className="text-xs uppercase tracking-wide text-slate-500">
          {t('support.my_tickets')} <span className="text-slate-300">›</span> {t('support.detail.details_title')}
        </p>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-md">
              <FiMessageSquare className="h-7 w-7" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-lg font-semibold tracking-tight text-slate-900 md:text-xl">
                  {ticket.ticket_number}
                </h1>
                <span
                  className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusTone(ticket.status?.name)}`}
                >
                  {ticket.status?.name ?? '-'}
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-700">{ticket.subject}</p>
              <p className="mt-2 text-xs text-slate-500">
                {t('support.list.created_at')}: {format(new Date(ticket.created_at), 'dd.MM.yyyy HH:mm')}
                <span className="mx-1.5 text-slate-300">•</span>
                {t('support.detail.updated_at')}: {format(new Date(ticket.updated_at), 'dd.MM.yyyy HH:mm')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:min-w-[420px]">
            <div className="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2.5">
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                {t('support.list.priority')}
              </p>
              <p className="mt-1 flex items-center gap-2 text-sm font-medium text-slate-900">
                <span className={`h-2 w-2 rounded-full ${priorityDot(ticket.priority?.name)}`} />
                {ticket.priority?.name ?? '-'}
              </p>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2.5">
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                {t('support.list.category')}
              </p>
              <p className="mt-1 flex items-center gap-2 text-sm font-medium text-slate-900">
                <FiGrid className="h-3.5 w-3.5 text-slate-400" />
                {ticket.category?.name ?? '-'}
              </p>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2.5">
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                {t('support.detail.source')}
              </p>
              <p className="mt-1 flex items-center gap-2 text-sm font-medium text-slate-900">
                <FiGlobe className="h-3.5 w-3.5 text-slate-400" />
                {ticket.source_app?.toUpperCase()}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
        <h2 className="mb-1 text-base font-semibold text-slate-900">{t('support.detail.details_title')}</h2>
        <dl>
          <DetailRow
            icon={<FiMessageSquare className="h-4 w-4" />}
            label={t('support.list.subject')}
            value={ticket.subject}
          />
          <DetailRow
            icon={<FiFileText className="h-4 w-4" />}
            label={t('support.detail.description')}
            value={<span className="whitespace-pre-wrap">{ticket.description}</span>}
          />
          {ticket.reference_number ? (
            <DetailRow
              icon={<FiHash className="h-4 w-4" />}
              label={t('helpdesk.form.reference_number')}
              value={ticket.reference_number}
            />
          ) : null}
          {ticket.related_ticket ? (
            <DetailRow
              icon={<FiLink className="h-4 w-4" />}
              label={t('support.detail.related_ticket')}
              value={`${ticket.related_ticket.ticket_number} — ${ticket.related_ticket.subject}`}
            />
          ) : null}
          <DetailRow
            icon={<FiPaperclip className="h-4 w-4" />}
            label={t('support.list.attachments')}
            value={
              ticket.attachments?.length ? (
                <button
                  type="button"
                  onClick={() => setShowAttachments(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  <FiPaperclip className="h-4 w-4 text-[#006EAB]" />
                  {t('support.detail.view_attachments')} ({ticket.attachments.length})
                </button>
              ) : (
                t('support.detail.no_attachments')
              )
            }
          />
        </dl>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:p-5 lg:col-span-3">
          <h2 className="mb-3 text-base font-semibold text-slate-900">{t('support.detail.communication_title')}</h2>

          <div className="mb-4 max-h-[420px] space-y-3 overflow-y-auto pr-1">
            {(ticket.messages ?? []).length === 0 ? (
              <p className="text-sm text-slate-500">{t('support.detail.no_messages')}</p>
            ) : (
              (ticket.messages ?? []).map((message) => (
                <article
                  key={message.message_id}
                  className={`rounded-xl border p-3 ${messageBubbleClass(message.author_type)}`}
                >
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
                        message.author_type === 'system'
                          ? 'bg-sky-200 text-sky-800'
                          : message.author_type === 'support'
                            ? 'bg-emerald-200 text-emerald-900'
                            : 'bg-slate-200 text-slate-700'
                      }`}
                    >
                      {message.author_type === 'system' ? (
                        <FiSettings className="h-3.5 w-3.5" />
                      ) : (
                        (message.author_name?.[0] ?? '?').toUpperCase()
                      )}
                    </div>
                    <span className="text-sm font-medium text-slate-900">{message.author_name}</span>
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${authorBadgeClass(message.author_type)}`}
                    >
                      {authorBadgeLabel(message.author_type, t)}
                    </span>
                    <span className="text-xs text-slate-500">
                      {format(new Date(message.created_at), 'dd.MM.yyyy HH:mm')}
                    </span>
                  </div>
                  {message.body_html ? (
                    <div
                      className="ql-editor !overflow-visible !p-0 text-sm text-slate-800 [&_a]:text-[#006EAB] [&_a]:underline [&_figure]:my-3 [&_figure]:rounded-lg [&_figure]:border [&_figure]:border-slate-200 [&_figure]:bg-white [&_figure]:p-2 [&_figure_figcaption]:mt-1 [&_figure_figcaption]:text-xs [&_figure_figcaption]:text-slate-500 [&_figure_img]:mx-auto [&_figure_img]:max-h-96 [&_figure_img]:max-w-full [&_figure_img]:rounded-md [&_figure_video]:mx-auto [&_figure_video]:max-h-96 [&_figure_video]:max-w-full"
                      dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(message.body_html) }}
                    />
                  ) : (
                    <p className="whitespace-pre-wrap text-sm text-slate-800">{message.body}</p>
                  )}
                  <MessageSignature
                    authorType={message.author_type}
                    includeSignatureLogo={message.include_signature_logo}
                    logoUrl={ticket.reply_signature_logo_url ?? null}
                  />
                  <MessageAttachments
                    files={(message.attachments ?? []).filter((attachment) => !attachment.is_inline)}
                  />
                </article>
              ))
            )}
          </div>

          {!isClosed ? (
            <HelpDeskReplyComposer
              messageBody={messageBody}
              messageHtml={messageHtml}
              messageAttachments={messageAttachments}
              isSending={isSending}
              onMessageChange={handleMessageChange}
              onMessageAttachmentsChange={setMessageAttachments}
              onSendMessage={handleSendMessage}
            />
          ) : (
            <p className="border-t border-slate-100 pt-4 text-sm text-slate-500">
              {t('support.detail.closed_readonly')}
            </p>
          )}
        </section>

        <section className="h-fit rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:p-5 lg:col-span-2">
          <nav className="mb-4 flex rounded-lg bg-slate-100 p-1" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={additionalInfoTab === 'information'}
              onClick={() => setAdditionalInfoTab('information')}
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-2 py-2 text-sm font-medium ${
                additionalInfoTab === 'information'
                  ? 'bg-white text-[#006EAB] shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <FiInfo className="h-4 w-4" />
              {t('support.detail.information_tab')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={additionalInfoTab === 'history'}
              onClick={() => setAdditionalInfoTab('history')}
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-2 py-2 text-sm font-medium ${
                additionalInfoTab === 'history'
                  ? 'bg-white text-[#006EAB] shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <FiClock className="h-4 w-4" />
              {t('support.detail.status_history_button')}
            </button>
          </nav>

          {additionalInfoTab === 'information' ? (
            <dl className="space-y-3 text-sm">
              <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
                <dt className="flex items-center gap-2 text-slate-500">
                  <FiGlobe className="h-4 w-4" />
                  {t('support.detail.source')}
                </dt>
                <dd className="font-medium text-slate-900">{ticket.source_app?.toUpperCase()}</dd>
              </div>
              <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
                <dt className="flex items-center gap-2 text-slate-500">
                  <FiGrid className="h-4 w-4" />
                  {t('support.list.category')}
                </dt>
                <dd className="text-right font-medium text-slate-900">{ticket.category?.name ?? '-'}</dd>
              </div>
              <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
                <dt className="flex items-center gap-2 text-slate-500">
                  <FiClipboard className="h-4 w-4" />
                  {t('support.detail.reply_count')}
                </dt>
                <dd className="font-medium text-slate-900">
                  {(ticket.messages ?? []).filter((message) => message.author_type !== 'system').length}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="flex items-center gap-2 text-slate-500">
                  <FiPaperclip className="h-4 w-4" />
                  {t('support.list.attachments')}
                </dt>
                <dd className="font-medium text-slate-900">
                  {ticket.attachments?.length ? (
                    <button
                      type="button"
                      onClick={() => setShowAttachments(true)}
                      className="text-[#006EAB] hover:underline"
                    >
                      {ticket.attachments.length}
                    </button>
                  ) : (
                    0
                  )}
                </dd>
              </div>
            </dl>
          ) : isHistoryLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : isHistoryError ? (
            <p className="text-sm text-red-600">{t('support.detail.status_history_error')}</p>
          ) : !statusHistory?.length ? (
            <p className="text-sm text-slate-500">{t('support.detail.status_history_empty')}</p>
          ) : (
            <>
              <ol className="relative ml-1 border-l-2 border-slate-200 pl-6">
                {statusHistory.slice(0, 5).map((entry, index) => (
                  <li key={`${entry.created_at}-${index}`} className="relative pb-5 last:pb-0">
                    <span className="absolute -left-[31px] top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-white ring-4 ring-white">
                      <span className="h-3 w-3 rounded-full bg-[#006EAB] ring-2 ring-sky-100" />
                    </span>
                    <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                      <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                        {entry.from_status?.name ? (
                          <>
                            <span className="rounded-md bg-white px-2 py-0.5 text-slate-600">
                              {entry.from_status.name}
                            </span>
                            <span className="text-slate-400">→</span>
                          </>
                        ) : null}
                        <span className="rounded-md bg-sky-100 px-2 py-0.5 text-[#006EAB]">
                          {entry.to_status?.name}
                        </span>
                      </p>
                      <p className="mt-1.5 text-xs text-slate-500">
                        {entry.changed_by_name}
                        <span className="mx-1.5 text-slate-300">•</span>
                        {format(new Date(entry.created_at), 'dd.MM.yyyy HH:mm')}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
              {statusHistory.length > 5 ? (
                <button
                  type="button"
                  onClick={() => setShowStatusHistory(true)}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-[#006EAB] hover:bg-sky-100"
                >
                  {t('support.detail.show_more')}
                  <FiArrowRight className="h-4 w-4" />
                </button>
              ) : null}
            </>
          )}
        </section>
      </div>

      <AttachmentsViewModal files={ticket.attachments ?? []} isOpen={showAttachments} setIsOpen={setShowAttachments} />
      <StatusHistoryModal
        isOpen={showStatusHistory}
        setIsOpen={setShowStatusHistory}
        history={statusHistory}
        isLoading={isHistoryLoading}
        isError={isHistoryError}
      />
    </div>
  )
}

export default HelpDeskTicketDetail
